import { Ticket } from "./Ticket";

export class TicketMapper {
  id: number;
  numbers: boolean[];
  stars: boolean[];

  constructor(id: number, numbers: boolean[], stars: boolean[]) {
    this.id = id;
    this.numbers = numbers;
    this.stars = stars;
  }

  static random(): TicketMapper {
    // generate 5 numbers and 2 stars
    const numbers = new Array<boolean>(50).fill(false);
    const stars = new Array<boolean>(12).fill(false);
    const selectedNumbers: bigint[] = [];
    const selectedStars: bigint[] = [];

    while (selectedNumbers.length < 5) {
      const number = Math.floor(Math.random() * 50) + 1;
      if (!selectedNumbers.includes(BigInt(number))) {
        selectedNumbers.push(BigInt(number));
        numbers[number - 1] = true;
      }
    }

    while (selectedStars.length < 2) {
      const star = Math.floor(Math.random() * 12) + 1;
      if (!selectedStars.includes(BigInt(star))) {
        selectedStars.push(BigInt(star));
        stars[star - 1] = true;
      }
    }

    return new TicketMapper(0, numbers, stars);
  }

  /**
   * Check if all numbers are selected.
   * @param numbersPerTicket Numbers requried per ticket.
   * @returns A boolean indicating whether all numbers are selected or not.
   */
  areAllNumbersSelected(numbersPerTicket: number): boolean {
    const selectedNumbers = this.numbers.filter((num) => num).length;
    return selectedNumbers === numbersPerTicket;
  }

  /**
   * Check if all stars are selected.
   * @param starsPerTicket Stars requried per ticket.
   * @returns A boolean indicating whether all stars are selected or not.
   */
  areAllStarsSelected(starsPerTicket: number): boolean {
    const selectedStars = this.stars.filter((star) => star).length;
    return selectedStars === starsPerTicket;
  }

  /**
   * Check if the ticket mapper is complete. A ticket mapper is complete if
   * all required numbers and stars are selected.
   * @param numbersPerTicket Numbers requried per ticket.
   * @param starsPerTicket Stars required per ticket.
   * @returns A boolean indicating whether the ticket is complete or not.
   */
  isComplete(numbersPerTicket: number, starsPerTicket: number): boolean {
    return (
      this.areAllNumbersSelected(numbersPerTicket) &&
      this.areAllStarsSelected(starsPerTicket)
    );
  }

  /**
   * Check if the ticket mapper is started. A ticket mapper is started if
   * any number or star is selected.
   * @returns A boolean indicating whether the ticket is started or not.
   */
  isStarted(): boolean {
    return this.numbers.some((num) => num) || this.stars.some((star) => star);
  }

  /**
   * Check if ticket mapper is valid. Valid means being either complete,
   * or not started at all.
   * @param numbersPerTicket Numbers requried per ticket.
   * @param starsPerTicket Stars required per ticket.
   * @returns A boolean indicating whether the ticket is valid or not.
   */
  isValid(numbersPerTicket: number, starsPerTicket: number): boolean {
    const isComplete = this.isComplete(
      Number(numbersPerTicket),
      Number(starsPerTicket),
    );
    const isStarted = this.isStarted();
    return !isStarted || isComplete;
  }

  /**
   * Convert the ticket mapper to a ticket.
   * @param numbersPerTicket Numbers requried per ticket.
   * @param starsPerTicket Stars required per ticket.
   * @returns A __{@link Ticket}__ object.
   */
  toTicket(numbersPerTicket: number, starsPerTicket: number): Ticket | null {
    if (!this.isComplete(numbersPerTicket, starsPerTicket)) {
      return null;
    }
    const selectedNumbers: bigint[] = this.numbers
      .map((isSelected, index) => (isSelected ? BigInt(index + 1) : null))
      .filter((num) => num !== null);

    const selectedStars: bigint[] = this.stars
      .map((isSelected, index) => (isSelected ? BigInt(index + 1) : null))
      .filter((star) => star !== null);

    return new Ticket(selectedNumbers, selectedStars);
  }

  static empty(id: number, numberPool: number, starPool: number): TicketMapper {
    return new TicketMapper(
      id,
      new Array<boolean>(numberPool).fill(false),
      new Array<boolean>(starPool).fill(false),
    );
  }

  toString(): string {
    return `TicketMapper(id: ${this.id}, numbers: ${this.numbers}, stars: ${this.stars})`;
  }
}
